/**
 * Extracts context from the host page.
 *
 * The rule this file exists to enforce: never send the DOM. A full page dump
 * is expensive, mostly navigation chrome, and routinely contains things a host
 * would be alarmed to see leave the browser - a signed-in user's name, an
 * account number, the contents of a half-filled form.
 *
 * So extraction is narrow and subtractive. Only the main content region is
 * read, known-sensitive regions and every form control are removed first, and
 * the result is capped well below anything that could matter.
 */

import { PageAction, PageContext, PageEvent, PageLink, ScreenContext } from './types';

const MAX_CONTENT_CHARS = 3000;

/** Regions that are chrome, not content. */
const CHROME_SELECTORS = [
    'script',
    'style',
    'noscript',
    'template',
    'svg',
    'nav',
    'header',
    'footer',
    'aside',
    'iframe',
    'form',
    'input',
    'textarea',
    'select',
    'button',
    '[aria-hidden="true"]',
    '[hidden]',
    '[data-chatbot-ignore]'
];

/**
 * Attribute and class hints that mark a region as sensitive.
 *
 * Matched loosely and on purpose: a false positive costs a little context, a
 * false negative exfiltrates a card number.
 */
const SENSITIVE_HINT =
    /(password|secret|token|api[-_]?key|credit|card|cvv|iban|ssn|passport|otp|private|account[-_]?number|auth)/i;

/** Where a page's real content usually lives, in order of preference. */
const CONTENT_SELECTORS = [
    '[data-chatbot-content]',
    'main',
    '[role="main"]',
    'article',
    '#content',
    '#main',
    '.main-content'
];

export function collectPageContext(): PageContext {
    const context: PageContext = {};

    try {
        // Path only. A query string is where reset tokens and identifiers
        // live, and the server has no use for them.
        context.url = `${window.location.pathname}${window.location.hash}`.slice(
            0,
            2048
        );
        context.title = (document.title || '').trim().slice(0, 300);

        const description = document
            .querySelector('meta[name="description"], meta[property="og:description"]')
            ?.getAttribute('content');
        if (description) {
            context.description = description.trim().slice(0, 1000);
        }

        const breadcrumbs = collectBreadcrumbs();
        if (breadcrumbs.length > 0) {
            context.breadcrumbs = breadcrumbs;
        }

        const links = collectSameOriginLinks();
        if (links.length > 0) {
            context.links = links;
        }

        const events = collectEvents();
        if (events.length > 0) {
            context.events = events;
        }

        const actions = collectActions();
        if (actions.length > 0) {
            context.actions = actions;
        }

        const content = collectVisibleText();
        if (content.length >= 20) {
            context.content = content;
        }
    } catch {
        // A host page with an exotic CSP or a locked-down DOM must degrade to
        // "no context", never to a broken widget.
    }

    return context;
}

/** Screen identity for a mobile or SPA host that labels the current view. */
export function collectScreenContext(): ScreenContext | undefined {
    try {
        const marked = document.querySelector('[data-chatbot-screen]');
        if (marked === null) {
            return undefined;
        }
        const screen: ScreenContext = {};
        const id = marked.getAttribute('data-chatbot-screen');
        if (id !== null && id.trim() !== '') {
            screen.id = id.trim().slice(0, 120);
        }
        const name =
            marked.getAttribute('data-chatbot-screen-name') ??
            marked.getAttribute('aria-label');
        if (name !== null && name.trim() !== '') {
            screen.name = name.trim().slice(0, 200);
        }
        const description = marked.getAttribute('data-chatbot-screen-description');
        if (description !== null && description.trim() !== '') {
            screen.description = description.trim().slice(0, 1000);
        }
        return Object.keys(screen).length === 0 ? undefined : screen;
    } catch {
        return undefined;
    }
}

function collectBreadcrumbs(): string[] {
    const container = document.querySelector(
        '[aria-label*="readcrumb"], .breadcrumb, .breadcrumbs, [data-breadcrumb]'
    );
    if (container === null) {
        return [];
    }

    return Array.from(container.querySelectorAll('a, li, span'))
        .map((element) => (element.textContent ?? '').trim())
        .filter((text) => text.length > 0 && text.length < 80)
        .filter((text, index, all) => all.indexOf(text) === index)
        .slice(0, 8);
}

const MAX_PAGE_LINKS = 24;

/**
 * Collects same-origin (or root-relative) links from the page.
 *
 * These are the host site's own pages, not model-invented URLs. Query strings
 * are stripped for the same reason they are stripped from the page URL.
 */
function collectSameOriginLinks(): PageLink[] {
    const anchors = Array.from(document.querySelectorAll('a[href]'));
    const seen = new Set<string>();
    const links: PageLink[] = [];

    for (const node of anchors) {
        if (links.length >= MAX_PAGE_LINKS) {
            break;
        }
        if (!(node instanceof HTMLAnchorElement)) {
            continue;
        }
        if (node.closest('[data-chatbot-ignore], form, [aria-hidden="true"]')) {
            continue;
        }

        const href = node.getAttribute('href');
        if (href === null || href === '' || href.startsWith('javascript:')) {
            continue;
        }

        const normalized = normalizePageLink(href);
        if (normalized === null || seen.has(normalized)) {
            continue;
        }

        const title = (node.textContent ?? '').replace(/\s+/g, ' ').trim();
        if (title.length === 0 || title.length > 80) {
            continue;
        }

        seen.add(normalized);
        links.push({ title, url: normalized });
    }

    return links;
}

function normalizePageLink(href: string): string | null {
    try {
        const parsed = new URL(href, window.location.origin);
        if (parsed.origin !== window.location.origin) {
            return null;
        }
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return null;
        }
        const path = `${parsed.pathname}${parsed.hash}`.slice(0, 2048);
        return path === '' ? '/' : path;
    } catch {
        return null;
    }
}

/**
 * Reads the main content region as plain text.
 *
 * The subtree is cloned before stripping so the host page's own DOM is never
 * modified - the widget must be invisible to the application it sits in.
 */
function collectVisibleText(): string {
    let root: Element | null = null;
    for (const selector of CONTENT_SELECTORS) {
        root = document.querySelector(selector);
        if (root !== null) {
            break;
        }
    }
    if (root === null) {
        root = document.body;
    }
    if (root === null) {
        return '';
    }

    const clone = root.cloneNode(true) as Element;

    for (const selector of CHROME_SELECTORS) {
        clone.querySelectorAll(selector).forEach((element) => element.remove());
    }

    // Drop anything self-identifying as sensitive, by any of the attributes a
    // developer would plausibly have used to label it.
    clone.querySelectorAll('*').forEach((element) => {
        const signature = [
            element.className && typeof element.className === 'string'
                ? element.className
                : '',
            element.id,
            element.getAttribute('name') ?? '',
            element.getAttribute('data-testid') ?? '',
            element.getAttribute('autocomplete') ?? ''
        ].join(' ');

        if (SENSITIVE_HINT.test(signature)) {
            element.remove();
        }
    });

    return (clone.textContent ?? '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_CONTENT_CHARS);
}

const MAX_EVENTS = 20;
const MAX_ACTIONS = 16;
const EVENT_TYPE = /event/i;

function collectEvents(): PageEvent[] {
    const events: PageEvent[] = [];
    const seen = new Set<string>();

    const add = (event: PageEvent): void => {
        const title = event.title.replace(/\s+/g, ' ').trim();
        if (title.length < 3 || title.length > 200) {
            return;
        }
        const key = `${title}|${event.when ?? ''}|${event.where ?? ''}`;
        if (seen.has(key) || events.length >= MAX_EVENTS) {
            return;
        }
        seen.add(key);
        events.push({
            title,
            when: event.when,
            where: event.where,
            url: event.url
        });
    };

    collectJsonLdEvents(add);
    collectMicrodataEvents(add);
    collectTimeTagEvents(add);
    collectMarkedEvents(add);

    return events;
}

function collectJsonLdEvents(add: (event: PageEvent) => void): void {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (const script of Array.from(scripts)) {
        try {
            const parsed: unknown = JSON.parse(script.textContent ?? '');
            walkJsonLd(parsed, add);
        } catch {
            // Malformed JSON-LD is skipped; it must never break the widget.
        }
    }
}

function walkJsonLd(value: unknown, add: (event: PageEvent) => void): void {
    if (value === null || value === undefined) {
        return;
    }
    if (Array.isArray(value)) {
        for (const entry of value) {
            walkJsonLd(entry, add);
        }
        return;
    }
    if (typeof value !== 'object') {
        return;
    }

    const record = value as Record<string, unknown>;
    if (record['@graph'] !== undefined) {
        walkJsonLd(record['@graph'], add);
    }

    const typeValue = record['@type'];
    const types = Array.isArray(typeValue)
        ? typeValue.map((item) => String(item))
        : typeof typeValue === 'string'
          ? [typeValue]
          : [];

    if (types.some((type) => EVENT_TYPE.test(type))) {
        const location = record['location'];
        const locationName =
            typeof location === 'string'
                ? location
                : location !== null &&
                    typeof location === 'object' &&
                    typeof (location as { name?: unknown }).name === 'string'
                  ? String((location as { name: string }).name)
                  : undefined;
        const url = typeof record['url'] === 'string' ? record['url'] : undefined;
        add({
            title: typeof record['name'] === 'string' ? record['name'] : '',
            when:
                typeof record['startDate'] === 'string'
                    ? record['startDate']
                    : typeof record['startTime'] === 'string'
                      ? record['startTime']
                      : undefined,
            where: locationName,
            url: url !== undefined ? normalizePageLink(url) ?? undefined : undefined
        });
    }

    for (const nested of Object.values(record)) {
        if (nested !== null && typeof nested === 'object') {
            walkJsonLd(nested, add);
        }
    }
}

function collectMicrodataEvents(add: (event: PageEvent) => void): void {
    const nodes = document.querySelectorAll('[itemtype*="Event"], [itemtype*="event"]');
    for (const node of Array.from(nodes)) {
        if (node.closest('[data-chatbot-ignore]')) {
            continue;
        }
        const title =
            node.querySelector('[itemprop="name"]')?.textContent ??
            node.getAttribute('aria-label') ??
            '';
        const when =
            node.querySelector('time[datetime], [itemprop="startDate"]')?.getAttribute(
                'datetime'
            ) ??
            node.querySelector('[itemprop="startDate"]')?.textContent ??
            undefined;
        const where =
            node.querySelector('[itemprop="location"]')?.textContent ?? undefined;
        add({
            title,
            when: when?.trim(),
            where: where?.replace(/\s+/g, ' ').trim()
        });
    }
}

function collectTimeTagEvents(add: (event: PageEvent) => void): void {
    const times = document.querySelectorAll('time[datetime]');
    for (const node of Array.from(times)) {
        if (node.closest('[data-chatbot-ignore], [data-universal-chatbot]')) {
            continue;
        }
        const when = node.getAttribute('datetime') ?? undefined;
        const heading = node.closest('article, li, section, .event, [data-event]');
        const title =
            heading?.querySelector('h1, h2, h3, h4, [itemprop="name"]')?.textContent ??
            node.textContent ??
            '';
        add({ title, when });
    }
}

function collectMarkedEvents(add: (event: PageEvent) => void): void {
    const nodes = document.querySelectorAll(
        '[data-chatbot-event], [data-event], .event, .events-item'
    );
    for (const node of Array.from(nodes)) {
        if (node.closest('[data-chatbot-ignore]')) {
            continue;
        }
        const title =
            node.getAttribute('data-event-title') ??
            node.querySelector('h1, h2, h3, h4, .title, .event-title')?.textContent ??
            node.getAttribute('aria-label') ??
            '';
        const when =
            node.getAttribute('data-event-when') ??
            node.querySelector('time[datetime]')?.getAttribute('datetime') ??
            undefined;
        const where =
            node.getAttribute('data-event-where') ??
            node.querySelector('.location, .venue, [itemprop="location"]')?.textContent ??
            undefined;
        add({
            title,
            when: when?.trim(),
            where: where?.replace(/\s+/g, ' ').trim()
        });
    }
}

function collectActions(): PageAction[] {
    const actions: PageAction[] = [];
    const seen = new Set<string>();
    const candidates = document.querySelectorAll(
        'a[href], button, [role="button"], input[type="submit"], input[type="button"]'
    );

    for (const node of Array.from(candidates)) {
        if (actions.length >= MAX_ACTIONS) {
            break;
        }
        if (
            node.closest(
                '[data-chatbot-ignore], [data-universal-chatbot], form, [aria-hidden="true"]'
            )
        ) {
            continue;
        }
        if (SENSITIVE_HINT.test(node.className + node.id)) {
            continue;
        }

        const label = (
            node.getAttribute('aria-label') ??
            node.getAttribute('value') ??
            node.textContent ??
            ''
        )
            .replace(/\s+/g, ' ')
            .trim();

        if (label.length < 2 || label.length > 80 || seen.has(label.toLowerCase())) {
            continue;
        }
        seen.add(label.toLowerCase());
        actions.push({ label });
    }

    return actions;
}
